import { useAppDispatch, useAppSelector } from '../useReduxToolkit';
import { getLogger } from '../../libs/logger';
import { useStateChangeGuard } from '../useStateChangeEffect/useStateChangeGuard';
import {
  ScreenshareState,
  type ScreenshareStates,
  ScreenshareTransition,
} from '../../features/screenshare/screenshare.types';
import { getScreenshareState } from '../../features/screenshare/screenshare.selectors';
import {
  setCurrentRequestedMember,
  setTransition,
} from '../../features/screenshare/screenshare.slice';
import { useSendScreenshareRequest } from './transitions/useSendScreenshareRequest';
import { useAcceptScreenshareRequest } from './transitions/useAcceptScreenshareRequest';
import { useRejectScreenshareRequest } from './transitions/useRejectScreenshareRequest';
import type {
  ScreenshareEventData,
  TransitionFunc,
} from './screenshareState.types';
import { useCancelScreenshareRequest } from './transitions/useCancelScreenshareRequest';
import { useChoosingWindowToShare } from './states/useChoosingWindowToShare';
import { closeModal, openModal } from '../../features/modals/modals.slice';
import { ModalId } from '../../features/modals/modals.type';
import { useLoadingRemoteStream } from './states/useLoadingRemoteStream';
import { useScreenshareInProgress } from './states/useScreenshareInProgress';

import { getApolloSessionDetails } from '../../features/apolloSession/apolloSession.selectors';
import { RealtimeRole } from '@doxyme/realtime';
import { useStopScreenshareRequest } from './transitions/useStopScreenshareRequest';
import { useEffect, useState } from 'react';

const logger = getLogger('useScreenshareState');

type UseScreenshareStateReturnValue = {
  transition: TransitionFunc;
};

let isListenerSlotTaken = false;

export const useScreenshareState = (): UseScreenshareStateReturnValue => {
  const screenshareState = useAppSelector(getScreenshareState);
  const dispatch = useAppDispatch();
  const { role } = useAppSelector(getApolloSessionDetails);

  const sendScreenshareRequest = useSendScreenshareRequest();
  const acceptScreenshareRequest = useAcceptScreenshareRequest();
  const rejectScreenshareRequest = useRejectScreenshareRequest();
  const cancelScreenshareRequest = useCancelScreenshareRequest();
  const choosingWindowToShare = useChoosingWindowToShare(onTransition);
  const loadingRemoteStream = useLoadingRemoteStream(onTransition);
  const screenshareInProgress = useScreenshareInProgress(onTransition);
  const stopScreenshareRequest = useStopScreenshareRequest();

  const [isInstanceListener, setIsInstanceListener] = useState(false);
  const checkDidStateChange = useStateChangeGuard<ScreenshareState>();

  useEffect(() => {
    if (!isListenerSlotTaken) {
      isListenerSlotTaken = true;
      setIsInstanceListener(true);
    }

    return () => {
      if (isInstanceListener) {
        isListenerSlotTaken = false;
      }
    };
  }, [isInstanceListener]);

  const onStateChange = async (state: ScreenshareStates): Promise<void> => {
    logger.debug('[screenshare/onStateChange] Debug:', { state });
    try {
      switch (state) {
        case ScreenshareState.ChoosingWindowToShare: {
          await choosingWindowToShare();
          break;
        }
        case ScreenshareState.InitializationError: {
          // show error dialog, yet to be created
          break;
        }
        case ScreenshareState.WaitingForRequestResponse: {
          break;
        }
        case ScreenshareState.LoadingRemoteStream: {
          void loadingRemoteStream();
          break;
        }
        case ScreenshareState.LoadingLocalStream: {
          // at the moment we can skip the loading local stream state and go straight into progress
          await onTransition(ScreenshareTransition.LOADING_STREAM_DONE);
          break;
        }
        case ScreenshareState.InProgress: {
          void screenshareInProgress();
          break;
        }

        default:
      }
    } catch (error) {
      if (
        error instanceof Error &&
        error.name === 'OT_USER_MEDIA_ACCESS_DENIED'
      ) {
        logger.debug(
          'User cancelled the screenshare when selecting the screen, or they are not allowed to share their screen',
        );
      } else {
        throw error;
      }
    }
  };

  async function onTransition(
    type: ScreenshareTransition,
    eventData?: ScreenshareEventData,
  ): Promise<void> {
    logger.info('[screenshare/onTransition]', {
      state: screenshareState,
      type,
      role,
      eventData,
    });
    switch (type) {
      case ScreenshareTransition.SEND_SCREENSHARE_REQUEST: {
        if (eventData?.realtimeMemberId !== undefined) {
          await sendScreenshareRequest(eventData.realtimeMemberId);
        } else {
          logger.error('[Screenshare] eventData.realtimeMemberId is undefined');
        }
        break;
      }
      case ScreenshareTransition.PERMISSIONS_ERROR:
      case ScreenshareTransition.REJECT_SCREENSHARE_REQUEST: {
        if (role === RealtimeRole.Patient) {
          await rejectScreenshareRequest(eventData);
          dispatch(closeModal(ModalId.Screenshare));
        }
        break;
      }
      case ScreenshareTransition.ACCEPT_SCREENSHARE_REQUEST: {
        if (role === RealtimeRole.Patient) {
          await onTransition(ScreenshareTransition.INITIALIZE);
          return;
        }
        break;
      }
      case ScreenshareTransition.CANCEL_SCREENSHARE_REQUEST: {
        await cancelScreenshareRequest(eventData);
        break;
      }
      case ScreenshareTransition.STARTING_STREAM: {
        if (role === RealtimeRole.Patient) {
          void acceptScreenshareRequest();
        }
        dispatch(closeModal(ModalId.Screenshare));
        break;
      }
      case ScreenshareTransition.LOADING_STREAM_DONE: {
        dispatch(closeModal(ModalId.Screenshare));
        break;
      }
      case ScreenshareTransition.STOP_SCREENSHARE: {
        // TODO: decouple in states for InProgress and RemoteInProgress
        if (
          screenshareState === ScreenshareState.InProgress ||
          screenshareState === ScreenshareState.LoadingRemoteStream
        ) {
          await stopScreenshareRequest(eventData);
        }
        break;
      }
      case ScreenshareTransition.RECEIVED_SCREENSHARE_REQUEST: {
        dispatch(setCurrentRequestedMember(eventData?.realtimeMemberId));
        dispatch(openModal(ModalId.Screenshare));
        break;
      }

      default:
    }

    dispatch(setTransition({ type, eventData }));
  }

  useEffect(() => {
    if (!isInstanceListener) return;
    if (checkDidStateChange(screenshareState)) {
      void onStateChange(screenshareState);
    }
  }, [isInstanceListener, screenshareState, checkDidStateChange]);

  return {
    transition: onTransition,
  };
};
